import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { DownloadCloud, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function ExportDialog({ open, onOpenChange, entityLabel = "student", fieldsUrl, onExport }) {
  const [fields, setFields] = useState([]);
  const [udiseAvail, setUdiseAvail] = useState(false);
  const [checked, setChecked] = useState(new Set());
  const [format, setFormat] = useState("xlsx");
  const [busy, setBusy] = useState("");

  useEffect(() => {
    if (!open) return;
    setChecked(new Set());
    setFormat("xlsx");
    api.get(fieldsUrl).then((r) => { setFields(r.data.fields || []); setUdiseAvail(!!r.data.udise); }).catch(() => setFields([]));
  }, [open, fieldsUrl]);

  const allChecked = fields.length > 0 && checked.size === fields.length;
  const toggleAll = () => setChecked(allChecked ? new Set() : new Set(fields.map((f) => f.key)));
  const toggle = (k) => { const n = new Set(checked); n.has(k) ? n.delete(k) : n.add(k); setChecked(n); };

  const run = async (mode) => {
    if (mode === "selected" && checked.size === 0) { toast.error("Select at least one field"); return; }
    setBusy(mode);
    try {
      await onExport({ mode, format, fields: [...checked] });
      toast.success("Export ready");
      onOpenChange(false);
    } catch { toast.error("Export failed"); }
    finally { setBusy(""); }
  };

  const IconBtn = ({ mode }) => (
    <button onClick={() => run(mode)} disabled={!!busy} data-testid={`export-${mode}-btn`}
      className="h-9 w-9 rounded-lg bg-sky-500 hover:bg-sky-600 text-white flex items-center justify-center disabled:opacity-50 shrink-0 transition-colors">
      {busy === mode ? <Loader2 size={17} className="animate-spin" /> : <DownloadCloud size={17} />}
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="export-dialog">
        <DialogHeader><DialogTitle className="text-2xl font-heading">Export Data</DialogTitle></DialogHeader>

        {udiseAvail && (
          <>
            <div className="flex items-center justify-between border-b border-slate-100 pb-4">
              <h4 className="font-bold text-slate-800">Download in UDISE+ Format</h4>
              <IconBtn mode="udise" />
            </div>
            <div className="text-xs text-slate-400 font-semibold">OR</div>
          </>
        )}

        <p className="text-sm text-slate-500">You can download all {entityLabel} data or filtered data based on your selection.</p>
        <div className="flex items-center gap-4">
          <span className="text-sm font-medium text-slate-600">Download data in:</span>
          <label className="flex items-center gap-1.5 text-sm cursor-pointer">
            <input type="radio" name="exp-format" checked={format === "xlsx"} onChange={() => setFormat("xlsx")} data-testid="export-format-excel" />Excel
          </label>
          <label className="flex items-center gap-1.5 text-sm cursor-pointer">
            <input type="radio" name="exp-format" checked={format === "pdf"} onChange={() => setFormat("pdf")} data-testid="export-format-pdf" />PDF
          </label>
        </div>

        <div className="flex items-center justify-between border-y border-slate-100 py-3">
          <h4 className="font-bold text-slate-800">Download All Data</h4>
          <IconBtn mode="all" />
        </div>
        <div className="text-xs text-slate-400 font-semibold">OR</div>

        <div className="flex items-center justify-between">
          <h4 className="font-bold text-slate-800">Download Selected Fields</h4>
          <label className="flex items-center gap-2 text-sm cursor-pointer font-medium" data-testid="export-select-all">
            <Checkbox checked={allChecked} onCheckedChange={toggleAll} />Select All
          </label>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1">
          {fields.map((f) => (
            <label key={f.key} className="flex items-center gap-2 text-sm cursor-pointer py-1" data-testid={`export-field-${f.key}`}>
              <Checkbox checked={checked.has(f.key)} onCheckedChange={() => toggle(f.key)} />
              <span className="truncate text-slate-600">{f.label}</span>
            </label>
          ))}
        </div>
        <div className="flex justify-end pt-2">
          <Button onClick={() => run("selected")} disabled={!!busy || checked.size === 0} className="gap-2" data-testid="export-selected-btn">
            {busy === "selected" ? <Loader2 size={16} className="animate-spin" /> : <DownloadCloud size={16} />}Download Selected ({checked.size})
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
